import React from 'react';
import PropTypes from 'prop-types';
import {
  Grid, Header, Button, Modal, Icon, Message,
} from 'semantic-ui-react';
import { isEmpty } from 'lodash';
import numeral from 'numeral';
import { isExpired, utcMoment } from '../../utilities/dateUtils';
import '../../assets/vraPendingStyle.css';

const formatCurrency = value => (value === null || value === undefined ? '' : `$ ${numeral(value).format('0,0')}`);
const formatDateTime = value => (value ? utcMoment(value).format('MM/DD/YYYY HH:mm') : '');

const VRAPendingDetails = props => {
  const {
    pendingVRA,
    isDetailsModalOpen,
    onCloseHandler,
    allowPendingVRA,
    invalidatePendingVRA,
    errorMessage,
    onErrorDismiss,
  } = props;

  const {
    id,
    odometerOnReturn,
    returnInitiatedAt,
    expirationDate,
    additionalInformation,
    auctionLocation,
    returningUser,
    pendingReasons,
    guaranteePurchase,
  } = pendingVRA;

  const guaranteeOffer = guaranteePurchase?.guaranteeOffer;
  const vehicleInformation = guaranteeOffer?.vehicleInformation;
  const auctionInformation = guaranteeOffer?.auctionInformation;
  const guaranteeExpirationDate = guaranteePurchase?.expiresAt;

  const vin = vehicleInformation?.vin;
  const returnLocation = auctionLocation?.initials || auctionLocation?.name;
  const pendingReasonNotices = isEmpty(pendingReasons)
    ? []
    : pendingReasons.map(pendingReason => pendingReason.notice);

  return (
    <Modal
      open={isDetailsModalOpen}
      closeIcon
      onClose={onCloseHandler}
      closeOnEscape={false}
    >
      <Modal.Header>VRA Pending Details</Modal.Header>
      <Modal.Content image scrolling>
        <Modal.Description>
          {errorMessage && (
            <Message
              negative
              onDismiss={onErrorDismiss}
              content={errorMessage}
            />
          )}
          <Grid>
            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>VRA:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{id}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>VIN:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{vin}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Price:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{formatCurrency(guaranteeOffer?.guaranteePrice)}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Vehicle Purchase Price:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{formatCurrency(auctionInformation?.vehiclePurchasePrice)}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Vehicle:</Header>
              </Grid.Column>
              <Grid.Column width={12}>
                {vehicleInformation?.year} {vehicleInformation?.make} {vehicleInformation?.model},
                {' '}{vehicleInformation?.odometerReading} miles, VIN {vin}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Pending Reason:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{pendingReasonNotices.join(', ')}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Dealer Name:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{guaranteeOffer?.dealerName}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Dealer Number:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{auctionInformation?.dealerNumber}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Return Location:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{returnLocation}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Odometer on Return:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{numeral(odometerOnReturn).format('0,0')}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Return Initiated At:</Header>
              </Grid.Column>
              <Grid.Column width={12}>{formatDateTime(returnInitiatedAt)}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Guarantee Expiration Date:</Header>
              </Grid.Column>
              <Grid.Column width={12}>
                {isExpired(guaranteeExpirationDate) ? (
                  <span className='expiredText'>
                    {formatDateTime(guaranteeExpirationDate)}
                    {' '}<Icon name='exclamation triangle' className='expiredIcon' />EXPIRED
                  </span>
                ) : (
                  guaranteeExpirationDate ? formatDateTime(guaranteeExpirationDate) : ''
                )}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Return Expiration Date:</Header>
              </Grid.Column>
              <Grid.Column width={12}>
                {isExpired(expirationDate) ? (
                  <span className='expiredText'>
                    {utcMoment(expirationDate).format('MM/DD/YYYY')}
                    {' '}<Icon name='exclamation triangle' className='expiredIcon' />EXPIRED
                  </span>
                ) : (
                  expirationDate ? utcMoment(expirationDate).format('MM/DD/YYYY') : ''
                )}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Return Initiated By:</Header>
              </Grid.Column>
              <Grid.Column width={12}>
                {returningUser?.email && (
                  <a href={`mailto:${returningUser.email}`}>{returningUser.email}</a>
                )}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={4}>
                <Header size='tiny'>Comments:</Header>
              </Grid.Column>
              <Grid.Column width={12} className='commentsColumn'>
                {additionalInformation}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button
          color='green'
          onClick={invalidatePendingVRA}
          inverted
        >
          <Icon name='checkmark' /> Invalidate
        </Button>
        <Button
          color='green'
          onClick={allowPendingVRA}
          inverted
        >
          <Icon name='checkmark' /> Allow
        </Button>
        <Button onClick={onCloseHandler}>Cancel</Button>
      </Modal.Actions>
    </Modal>
  );
};

VRAPendingDetails.propTypes = {
  pendingVRA: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    odometerOnReturn: PropTypes.number,
    returnInitiatedAt: PropTypes.string,
    expirationDate: PropTypes.string,
    additionalInformation: PropTypes.string,
    auctionLocation: PropTypes.shape({
      name: PropTypes.string,
      initials: PropTypes.string,
    }),
    returningUser: PropTypes.shape({
      email: PropTypes.string,
    }),
    pendingReasons: PropTypes.arrayOf(PropTypes.shape({
      notice: PropTypes.string,
    })),
    guaranteePurchase: PropTypes.shape({
      expiresAt: PropTypes.string,
      guaranteeOffer: PropTypes.shape({
        dealerName: PropTypes.string,
        guaranteePrice: PropTypes.number,
        vehicleInformation: PropTypes.shape({
          vin: PropTypes.string,
          year: PropTypes.number,
          make: PropTypes.string,
          model: PropTypes.string,
          odometerReading: PropTypes.number,
        }),
        auctionInformation: PropTypes.shape({
          dealerNumber: PropTypes.number,
          vehiclePurchasePrice: PropTypes.number,
        }),
      }),
    }),
  }).isRequired,
  isDetailsModalOpen: PropTypes.bool.isRequired,
  onCloseHandler: PropTypes.func.isRequired,
  allowPendingVRA: PropTypes.func.isRequired,
  invalidatePendingVRA: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  onErrorDismiss: PropTypes.func,
};

export default VRAPendingDetails;
